// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { UpdateOrgRequest } from "@chotca/config";
import { useTranslations } from "next-intl";
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  ImageGalleryEditor,
  Input,
  KycStatusBadge,
  LogoUploader,
  Select,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  formatDateTimeVn,
  useToast,
  type GalleryImage,
} from "@chotca/ui";
import {
  POSTING_STATE_LABEL_VI,
  VN_PHONE_REGEX,
  businessTypeOptions,
  type PostingState,
} from "@chotca/config";
import {
  useAdminActivityLog,
  useAdminClaimInvitations,
  useAdminPostings,
  useActivateOrg,
  useDeactivateOrg,
  useUploadAdminOrgGalleryImage,
  useUploadAdminOrgLogo,
} from "@chotca/api-client";
import { getSdk } from "@chotca/api-client";
import { qk } from "@chotca/api-client";
import { Copy, Trash2 } from "lucide-react";
import {
  DataTable,
  type Column,
  type RowAction,
} from "@/components/data-table";
import { AdminOrgBranchesCard } from "@/components/admin/org-branches-card";
import { AdminOrgMembersCard } from "@/components/admin/org-members-card";
import { OrgInterviewsCalendar } from "@/components/admin/org-interviews-calendar";
import {
  ActivityLogTable,
  type ActivityRow,
} from "@/components/activity-log-table";
import { useTabParam } from "@/lib/use-tab-param";
import { OverrideVerifyLevel } from "./override-verify-level";

// SCRUM-74 verification ladder labels.
const VERIFY_LEVEL_LABEL: Record<string, string> = {
  L1_CONTACT: "Đã xác minh liên hệ",
  L2_DOCUMENT: "Đã xác minh giấy tờ",
  L3_TRUSTED: "Nhà tuyển dụng uy tín",
};

export function OrgDetailClient({ orgId }: { orgId: string }) {
  const t = useTranslations("admin");
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editOpen, setEditOpen] = React.useState(false);
  const [tab, setTab] = useTabParam("info");

  // Fetch the org directly by id. (Previously this scanned page 1 of listOrgs
  // and find()'d by id, so any org past the first 20 by createdAt falsely
  // showed "không tìm thấy".)
  const orgQ = useQuery({
    queryKey: ["admin", "org-detail", orgId],
    queryFn: () =>
      getSdk()
        .admin.getOrg(orgId)
        .catch(() => null),
  });

  const updateOrg = useMutation({
    mutationFn: (body: UpdateOrgRequest) =>
      getSdk().admin.updateOrg(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "org-detail", orgId] });
      qc.invalidateQueries({ queryKey: ["admin", "orgs"] });
      toast({ tone: "success", title: "Đã lưu thay đổi" });
      setEditOpen(false);
    },
    onError: () => toast({ tone: "error", title: "Lưu thất bại" }),
  });

  // Block / unblock the employer = deactivate / reactivate the org (existing
  // mechanism — hides it from the feed and blocks employer actions).
  const deactivate = useDeactivateOrg();
  const activate = useActivateOrg();
  function refreshOrg() {
    qc.invalidateQueries({ queryKey: ["admin", "org-detail", orgId] });
    qc.invalidateQueries({ queryKey: ["admin", "orgs"] });
  }

  if (orgQ.isLoading) return <Skeleton className="h-96 rounded-2xl" />;
  if (!orgQ.data) {
    return (
      <p className="text-center text-sm text-ash">
        Không tìm thấy doanh nghiệp này.
      </p>
    );
  }
  const org = orgQ.data;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link to="/orgs" className="text-xs text-ash hover:text-ink">
            ← {t("orgsTitle")}
          </Link>
          <h1 className="mt-1 flex flex-wrap items-center gap-2 text-2xl font-bold text-ink">
            {org.name}
            {org.isShadow ? (
              <Badge tone="sun">{t("shadowBadge")}</Badge>
            ) : (
              <Badge tone="lime">{t("claimedBadge")}</Badge>
            )}
            <KycStatusBadge status={org.kycStatus} />
            {org.verifyLevel && org.verifyLevel !== "L0_NEW" ? (
              <Badge tone="ink">{VERIFY_LEVEL_LABEL[org.verifyLevel]}</Badge>
            ) : null}
            {org.deactivatedAt ? <Badge tone="ember">Đã khoá</Badge> : null}
          </h1>
          <p className="text-sm text-ash">
            {org.primaryAddress?.district ?? "—"} · {org.businessType}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <OverrideVerifyLevel orgId={orgId} current={org.verifyLevel} />
          <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
            Sửa thông tin
          </Button>
          {org.deactivatedAt ? (
            <Button
              variant="ghost"
              size="sm"
              loading={activate.isPending}
              onClick={() =>
                activate.mutate(orgId, {
                  onSuccess: () => {
                    refreshOrg();
                    toast({ tone: "success", title: "Đã mở khoá" });
                  },
                })
              }
            >
              Mở khoá
            </Button>
          ) : (
            <Button
              variant="danger"
              size="sm"
              loading={deactivate.isPending}
              onClick={() =>
                deactivate.mutate(orgId, {
                  onSuccess: () => {
                    refreshOrg();
                    toast({ tone: "success", title: "Đã khoá doanh nghiệp" });
                  },
                })
              }
            >
              Khoá doanh nghiệp
            </Button>
          )}
        </div>
      </header>

      <EditOrgDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        org={org}
        loading={updateOrg.isPending}
        onSubmit={(v) => updateOrg.mutate(v)}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="info">{t("tabInfo")}</TabsTrigger>
          <TabsTrigger value="branches">{t("tabBranches")}</TabsTrigger>
          <TabsTrigger value="members">{t("tabMembers")}</TabsTrigger>
          <TabsTrigger value="postings">{t("tabPostings")}</TabsTrigger>
          <TabsTrigger value="invitations">{t("tabInvitations")}</TabsTrigger>
          <TabsTrigger value="interviews">{t("tabInterviews")}</TabsTrigger>
          <TabsTrigger value="activity">{t("tabActivity")}</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <div className="space-y-6">
            <InfoCard org={org} />
            <OrgPlanCard orgId={orgId} />
            <AdminOrgLogoCard orgId={orgId} initialUrl={org.logoPublicUrl} />
            <AdminOrgGalleryCard
              orgId={orgId}
              initial={org.galleryImageKeys.map((key, i) => ({
                key,
                url: org.galleryPublicUrls[i] ?? "",
              }))}
            />
          </div>
        </TabsContent>

        <TabsContent value="branches">
          <AdminOrgBranchesCard orgId={orgId} />
        </TabsContent>

        <TabsContent value="members">
          <AdminOrgMembersCard orgId={orgId} />
        </TabsContent>

        <TabsContent value="postings">
          <PostingsTab orgId={orgId} />
        </TabsContent>

        <TabsContent value="invitations">
          <InvitationsTab orgId={orgId} />
        </TabsContent>

        <TabsContent value="interviews">
          <OrgInterviewsCalendar orgId={orgId} />
        </TabsContent>

        <TabsContent value="activity">
          <ActivityTab orgId={orgId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InfoCard({
  org,
}: {
  org: {
    id: string;
    name: string;
    primaryAddress: {
      address: string;
      district: string;
      city: string;
    } | null;
    contactName: string;
    contactPhone: string;
    contactEmail?: string | null;
    ownerEmail: string | null;
    ownerPhone: string | null;
    inviteCreditsAvailable: number;
    postingCount: number;
    createdAt: string;
  };
}) {
  // Sourcing balance now comes from the subscription plan status (not the old
  // interview-credit wallet).
  const planQ = useQuery({
    queryKey: ["admin", "org-plan", org.id],
    queryFn: () =>
      getSdk()
        .admin.getPlanStatus(org.id)
        .catch(() => null),
  });
  const sourcing = planQ.data?.sourcingRemaining;

  const addr = org.primaryAddress
    ? `${org.primaryAddress.address}, ${org.primaryAddress.district}, ${org.primaryAddress.city}`
    : "—";
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {[
        ["Địa chỉ (chi nhánh chính)", addr],
        ["Liên hệ", `${org.contactName} · ${org.contactPhone}`],
        ["Email liên hệ", org.contactEmail ?? "—"],
        ["Owner email", org.ownerEmail ?? "—"],
        ["Owner phone", org.ownerPhone ?? "—"],
        ["Lượt săn ứng viên còn", sourcing != null ? String(sourcing) : "…"],
        ["Số tin đã đăng", String(org.postingCount)],
        ["Tạo lúc", formatDateTimeVn(new Date(org.createdAt))],
      ].map(([k, v]) => (
        <div
          key={k as string}
          className="rounded-2xl border border-smoke bg-paper p-4"
        >
          <p className="text-xs uppercase text-ash">{k}</p>
          <p className="mt-1 text-sm font-medium text-ink">{v}</p>
        </div>
      ))}
    </div>
  );
}

// Subscription plan card: shows the org's current plan (effective, after
// expiry) + remaining sourcing/boost, and lets an admin grant/renew a plan
// directly (concierge — no payment). Replaces the old interview-credits card.
const PLAN_LABEL: Record<string, string> = {
  FREE: "Miễn phí",
  PRO: "Pro",
  BUSINESS: "Business",
};

function OrgPlanCard({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const planQ = useQuery({
    queryKey: ["admin", "org-plan", orgId],
    queryFn: () =>
      getSdk()
        .admin.getPlanStatus(orgId)
        .catch(() => null),
  });
  const [pick, setPick] = React.useState<"FREE" | "PRO" | "BUSINESS">("PRO");
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const subscribe = useMutation({
    mutationFn: (planCode: "FREE" | "PRO" | "BUSINESS") =>
      getSdk().admin.subscribeOrg(orgId, planCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "org-plan", orgId] });
      toast({ tone: "success", title: "Đã cập nhật gói" });
      setConfirmOpen(false);
    },
    onError: () => toast({ tone: "error", title: "Không cập nhật được gói" }),
  });

  const status = planQ.data;

  return (
    <div className="rounded-2xl border border-smoke bg-paper p-5">
      <h2 className="text-sm font-bold uppercase text-ash">Gói đăng ký</h2>
      {status ? (
        <div className="mt-3 space-y-1">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-ink">
              {PLAN_LABEL[status.effectiveCode] ?? status.effectiveCode}
            </span>
            {status.daysUntilExpiry != null ? (
              <span className="text-sm text-ash">
                còn {status.daysUntilExpiry} ngày
              </span>
            ) : (
              <span className="text-sm text-ash">không hết hạn</span>
            )}
          </div>
          <p className="text-xs text-ash">
            Còn {status.sourcingRemaining} lượt săn · {status.boostRemaining}{" "}
            boost trong kỳ
          </p>
        </div>
      ) : (
        <p className="mt-3 text-sm text-ash">Đang tải…</p>
      )}

      <div className="mt-5 border-t border-smoke pt-4">
        <h3 className="text-sm font-bold text-ink">Cấp / gia hạn gói</h3>
        <p className="mt-1 text-xs text-ash">
          Gán gói trực tiếp (không qua thanh toán). Cùng gói còn hạn sẽ cộng
          dồn; đổi gói sẽ bắt đầu kỳ mới.
        </p>
        <div className="mt-3 flex items-end gap-3">
          <FormField label="Gói">
            <Select
              value={pick}
              onValueChange={(v) => setPick(v as "FREE" | "PRO" | "BUSINESS")}
              options={[
                { value: "PRO", label: "Pro" },
                { value: "BUSINESS", label: "Business" },
                { value: "FREE", label: "Miễn phí (hạ gói)" },
              ]}
            />
          </FormField>
          <Button
            loading={subscribe.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            Cấp gói
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => subscribe.mutate(pick)}
        loading={subscribe.isPending}
        title={`Cấp gói ${PLAN_LABEL[pick] ?? pick} cho quán này?`}
        description={
          pick === "FREE"
            ? "Hạ về gói Miễn phí. Quán sẽ mất quyền săn ứng viên và hạn mức tin. Hành động này không qua thanh toán."
            : `Gán gói ${PLAN_LABEL[pick] ?? pick} trực tiếp (không qua thanh toán). Cùng gói còn hạn sẽ cộng dồn; đổi gói sẽ bắt đầu kỳ mới. Quán sẽ nhận email + thông báo xác nhận.`
        }
        confirmLabel="Cấp gói"
      />
    </div>
  );
}

// Admin can set / replace / clear an org's logo ("avatar") on its behalf.
// Persists immediately via PATCH /admin/orgs/:id { logoImageKey }. Worker
// mobile reads the resolved URL via the public org endpoint.
function AdminOrgLogoCard({
  orgId,
  initialUrl,
}: {
  orgId: string;
  initialUrl: string | null;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const upload = useUploadAdminOrgLogo(orgId);
  const [url, setUrl] = React.useState<string | null>(initialUrl);
  const save = useMutation({
    mutationFn: (logoImageKey: string | null) =>
      getSdk().admin.updateOrg(orgId, { logoImageKey }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "org-detail", orgId] });
      qc.invalidateQueries({ queryKey: ["admin", "orgs"] });
    },
  });

  return (
    <div className="rounded-2xl border border-smoke bg-paper p-5">
      <h2 className="text-sm font-bold uppercase text-ash">
        Logo doanh nghiệp
      </h2>
      <div className="mt-4">
        <LogoUploader
          url={url}
          onUpload={async (file, onProgress) => {
            const r = await upload.mutateAsync({ file, onProgress });
            return { key: r.key, url: r.publicUrl };
          }}
          onChange={async (next) => {
            const prev = url;
            setUrl(next.url);
            try {
              await save.mutateAsync(next.key);
            } catch {
              setUrl(prev);
              toast({ tone: "error", title: "Lưu thất bại" });
            }
          }}
          onRemove={async () => {
            const prev = url;
            setUrl(null);
            try {
              await save.mutateAsync(null);
            } catch {
              setUrl(prev);
              toast({ tone: "error", title: "Lưu thất bại" });
            }
          }}
          labels={{
            upload: "Tải logo lên",
            change: "Đổi logo",
            remove: "Xoá logo",
            uploadError: "Tải logo thất bại, vui lòng thử lại.",
            hint: "Logo hiển thị cho người tìm việc. Hình vuông, JPG, PNG hoặc WebP, tối đa 10MB.",
          }}
        />
      </div>
    </div>
  );
}

// Admin can add / reorder / remove an org's public gallery on its behalf.
// Each change persists immediately via PATCH /admin/orgs/:id, mirroring the
// employer-side autosave.
function AdminOrgGalleryCard({
  orgId,
  initial,
}: {
  orgId: string;
  initial: GalleryImage[];
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const upload = useUploadAdminOrgGalleryImage(orgId);
  const [images, setImages] = React.useState<GalleryImage[]>(initial);
  const save = useMutation({
    mutationFn: (keys: string[]) =>
      getSdk().admin.updateOrg(orgId, { galleryImageKeys: keys }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "org-detail", orgId] });
      qc.invalidateQueries({ queryKey: ["admin", "orgs"] });
    },
  });

  async function persist(next: GalleryImage[]) {
    const prev = images;
    setImages(next);
    try {
      await save.mutateAsync(next.map((i) => i.key));
    } catch {
      setImages(prev);
      toast({ tone: "error", title: "Lưu thất bại" });
    }
  }

  return (
    <div className="rounded-2xl border border-smoke bg-paper p-5">
      <h2 className="text-sm font-bold uppercase text-ash">Hình ảnh quán</h2>
      <div className="mt-4">
        <ImageGalleryEditor
          images={images}
          onUpload={async (file, onProgress) => {
            const r = await upload.mutateAsync({ file, onProgress });
            return { key: r.key, url: r.publicUrl };
          }}
          onChange={(next) => void persist(next)}
          labels={{
            add: "Thêm ảnh",
            counter: (count, max) => `${count}/${max} ảnh`,
            remove: "Xoá ảnh",
            moveBack: "Chuyển lên trước",
            moveForward: "Chuyển ra sau",
            uploadError: "Tải ảnh thất bại, vui lòng thử lại.",
            cover: "Ảnh bìa",
            hint: "Tối đa 10 ảnh. Ảnh đầu tiên là ảnh bìa. JPG, PNG hoặc WebP.",
          }}
        />
      </div>
    </div>
  );
}

// Org-scoped audit feed: every action logged against this org (postings,
// invites, wallet grants, member changes…), regardless of which member acted.
// The actor column stays visible since multiple people act within one org.
function ActivityTab({ orgId }: { orgId: string }) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const query = useAdminActivityLog({ orgId, page, limit: pageSize });
  const rows = (query.data?.items ?? []) as ActivityRow[];
  return (
    <ActivityLogTable
      rows={rows}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      page={page}
      pageSize={pageSize}
      total={query.data?.total}
      onPageChange={setPage}
      onPageSizeChange={(s) => {
        setPageSize(s);
        setPage(1);
      }}
      emptyText="Chưa có hoạt động nào."
    />
  );
}

function PostingsTab({ orgId }: { orgId: string }) {
  const t = useTranslations("admin");
  const { data, isLoading } = useAdminPostings({ orgId });
  if (isLoading) return <Skeleton className="h-40 rounded-2xl" />;
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Link
          to={`/postings/new?orgId=${orgId}`}
          className="inline-flex h-9 items-center justify-center rounded-full bg-ink px-4 text-sm font-semibold text-paper hover:bg-ink/90"
        >
          + {t("createPosting")}
        </Link>
      </div>
      {(data?.items ?? []).length === 0 ? (
        <p className="rounded-2xl border border-dashed border-smoke bg-paper p-6 text-center text-sm text-ash">
          {t("noPostings")}
        </p>
      ) : (
        <ul className="space-y-2">
          {data!.items.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between rounded-2xl border border-smoke bg-paper p-4"
            >
              <div>
                <Link
                  to={`/postings/${p.id}`}
                  className="text-sm font-bold text-ink hover:underline"
                >
                  {p.title}
                </Link>
                <p className="text-xs text-ash">
                  {p.applicantCount} {t("applicantCountLabel")}
                  {p.newApplicantCount > 0
                    ? ` · ${p.newApplicantCount} ${t("newApplicantCountLabel")}`
                    : ""}
                </p>
              </div>
              <Badge>
                {POSTING_STATE_LABEL_VI[p.state as PostingState] ?? p.state}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type InvitationRow = {
  id: string;
  email: string;
  expiresAt: string;
  claimedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
};

function InvitationsTab({ orgId }: { orgId: string }) {
  const t = useTranslations("admin");
  const { data, isLoading, isFetching } = useAdminClaimInvitations(orgId);
  const qc = useQueryClient();
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [pendingId, setPendingId] = React.useState<string | null>(null);

  function refresh() {
    qc.invalidateQueries({ queryKey: qk.admin.invitations(orgId) });
  }

  const create = useMutation({
    mutationFn: (form: { email: string; ttlHours: number }) =>
      getSdk().admin.createClaimInvitation(orgId, form),
    onSuccess: (inv) => {
      refresh();
      toast({
        tone: "success",
        title: "Đã gửi email mời",
        description: inv.link,
      });
      setCreateOpen(false);
    },
    onError: () => toast({ tone: "error", title: "Không thể tạo lời mời" }),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => getSdk().admin.revokeClaimInvitation(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      refresh();
      toast({ tone: "success", title: "Đã thu hồi" });
    },
  });

  function statusBadge(i: InvitationRow) {
    const expired = new Date(i.expiresAt) < new Date();
    if (i.claimedAt)
      return <Badge tone="lime">{t("claimsStatusClaimed")}</Badge>;
    if (i.revokedAt)
      return <Badge tone="smoke">{t("claimsStatusRevoked")}</Badge>;
    if (expired) return <Badge tone="ember-soft">{t("expired")}</Badge>;
    return <Badge tone="sun">{t("claimsStatusPending")}</Badge>;
  }

  function isActive(i: InvitationRow) {
    return !i.claimedAt && !i.revokedAt && new Date(i.expiresAt) >= new Date();
  }

  const items = (data?.items ?? []) as InvitationRow[];
  const columns: Column<InvitationRow>[] = [
    {
      key: "email",
      header: "Email",
      cell: (i) => <span className="font-mono text-xs">{i.email}</span>,
    },
    {
      key: "status",
      header: "Trạng thái",
      cell: (i) => statusBadge(i),
    },
    {
      key: "expires",
      header: "Hết hạn",
      cell: (i) => (
        <span className="text-xs">
          {formatDateTimeVn(new Date(i.expiresAt))}
        </span>
      ),
    },
    {
      key: "created",
      header: "Tạo lúc",
      cell: (i) => (
        <span className="text-xs text-ash">
          {formatDateTimeVn(new Date(i.createdAt))}
        </span>
      ),
    },
  ];

  const rowActions: RowAction<InvitationRow>[] = [
    {
      key: "copy",
      label: "Copy link (chỉ hiện 1 lần khi tạo)",
      icon: Copy,
      onClick: (i) =>
        toast({
          title: "Link chỉ hiển thị 1 lần khi tạo",
          description: `Tạo mới nếu cần lại — invitation #${i.id.slice(0, 8)}`,
        }),
      show: (i) => isActive(i),
    },
    {
      key: "revoke",
      label: "Thu hồi lời mời",
      icon: Trash2,
      destructive: true,
      onClick: (i) => revoke.mutate(i.id),
      show: (i) => isActive(i),
      loading: (i) => revoke.isPending && pendingId === i.id,
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(i) => i.id}
        rowActions={rowActions}
        emptyText={t("noInvitations")}
        headerActions={
          <Button onClick={() => setCreateOpen(true)}>
            + {t("inviteOwner")}
          </Button>
        }
      />
      <CreateInviteDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        loading={create.isPending}
        onSubmit={(v) => create.mutate(v)}
      />
    </>
  );
}

// Edit-form validation. Mirrors the constraints of `orgCoreFields` in
// @chotca/config but with Vietnamese, user-facing messages so a failed field
// can be highlighted and explained inline instead of the old blanket
// "Lưu thất bại" toast (which fired when the SDK's parse() threw on
// pre-existing invalid data — e.g. shadow orgs with an empty contactPhone).
// `taxCode` and `contactPhone` may be left blank (optional on the API).
const editOrgFormSchema = z.object({
  name: z
    .string()
    .min(1, "Vui lòng nhập tên doanh nghiệp")
    .max(120, "Tên doanh nghiệp tối đa 120 ký tự"),
  businessType: z
    .string()
    .min(1, "Vui lòng nhập loại hình")
    .max(60, "Loại hình tối đa 60 ký tự"),
  taxCode: z.string().max(20, "Mã số thuế tối đa 20 ký tự"),
  contactName: z
    .string()
    .min(1, "Vui lòng nhập tên người liên hệ")
    .max(120, "Tên người liên hệ tối đa 120 ký tự"),
  contactPhone: z
    .string()
    .refine((v) => v.trim() === "" || VN_PHONE_REGEX.test(v.trim()), {
      message:
        "SĐT không hợp lệ. Nhập số Việt Nam bắt đầu bằng 0 hoặc +84 (10 đến 11 chữ số), không khoảng trắng.",
    }),
  // Where shadow-org claim-invite + FOMO digest emails are sent (separate from
  // the owner's account email). Optional; empty clears it on the API.
  contactEmail: z
    .string()
    .refine(
      (v) => v.trim() === "" || z.string().email().safeParse(v.trim()).success,
      { message: "Email không hợp lệ" },
    ),
  verifiedTier: z.enum(["NONE", "TRUSTED", "SUPER"]),
});

type EditOrgFormValues = z.infer<typeof editOrgFormSchema>;

function EditOrgDialog({
  open,
  onClose,
  org,
  loading,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  org: {
    name: string;
    businessType: string;
    taxCode?: string | null;
    contactName: string;
    contactPhone: string;
    contactEmail?: string | null;
    verifiedTier?: "NONE" | "TRUSTED" | "SUPER";
  };
  loading: boolean;
  onSubmit: (v: UpdateOrgRequest) => void;
}) {
  const defaults = React.useMemo<EditOrgFormValues>(
    () => ({
      name: org.name,
      businessType: org.businessType,
      taxCode: org.taxCode ?? "",
      contactName: org.contactName,
      contactPhone: org.contactPhone,
      contactEmail: org.contactEmail ?? "",
      verifiedTier: org.verifiedTier ?? "NONE",
    }),
    [org],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditOrgFormValues>({
    resolver: zodResolver(editOrgFormSchema),
    mode: "onTouched",
    defaultValues: defaults,
  });

  // Re-seed the form whenever the dialog (re)opens or the org changes, so it
  // always reflects the latest data and clears stale validation state.
  React.useEffect(() => {
    if (open) reset(defaults);
  }, [open, defaults, reset]);

  // Only send fields the admin actually changed. Untouched-but-invalid data
  // (common on shadow orgs) is never re-submitted, so it can't block the save.
  const submit = (values: EditOrgFormValues) => {
    const next: UpdateOrgRequest = {};
    const name = values.name.trim();
    if (name !== org.name) next.name = name;
    const businessType = values.businessType.trim();
    if (businessType !== org.businessType) next.businessType = businessType;
    const taxCode = values.taxCode.trim() || null;
    if (taxCode !== (org.taxCode ?? null)) next.taxCode = taxCode;
    const contactName = values.contactName.trim();
    if (contactName !== org.contactName) next.contactName = contactName;
    const contactPhone = values.contactPhone.trim();
    if (contactPhone !== org.contactPhone) {
      next.contactPhone = contactPhone || undefined;
    }
    // Empty clears it (null); the API rejects "" under .email(), so never send it.
    const contactEmail = values.contactEmail.trim().toLowerCase();
    if (contactEmail !== (org.contactEmail ?? "")) {
      next.contactEmail = contactEmail || null;
    }
    onSubmit(next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Sửa thông tin doanh nghiệp"
      description="Địa chỉ được quản lý trong tab Chi nhánh, không sửa ở đây."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Huỷ
          </Button>
          <Button loading={loading} onClick={handleSubmit(submit)}>
            Lưu
          </Button>
        </div>
      }
    >
      <form className="space-y-3" onSubmit={handleSubmit(submit)} noValidate>
        <FormField label="Tên doanh nghiệp" error={errors.name?.message}>
          <Input invalid={!!errors.name} {...register("name")} />
        </FormField>
        <FormField
          label="Loại hình kinh doanh"
          error={errors.businessType?.message}
        >
          <Controller
            control={control}
            name="businessType"
            render={({ field }) => (
              <Select
                invalid={!!errors.businessType}
                value={field.value}
                onValueChange={field.onChange}
                placeholder="Chọn loại hình kinh doanh"
                options={businessTypeOptions(field.value)}
              />
            )}
          />
        </FormField>
        <FormField label="Hạng tin cậy (badge quán)">
          <Controller
            control={control}
            name="verifiedTier"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                options={[
                  { value: "NONE", label: "Không có" },
                  { value: "TRUSTED", label: "QUÁN UY TÍN" },
                  { value: "SUPER", label: "QUÁN SIÊU CẤP" },
                ]}
              />
            )}
          />
        </FormField>
        <FormField label="Mã số thuế" optional error={errors.taxCode?.message}>
          <Input
            invalid={!!errors.taxCode}
            placeholder="Không bắt buộc"
            {...register("taxCode")}
          />
        </FormField>
        <FormField label="Người liên hệ" error={errors.contactName?.message}>
          <Input invalid={!!errors.contactName} {...register("contactName")} />
        </FormField>
        <FormField
          label="SĐT liên hệ"
          optional
          error={errors.contactPhone?.message}
        >
          <Input
            invalid={!!errors.contactPhone}
            placeholder="VD: 0901234567"
            {...register("contactPhone")}
          />
        </FormField>
        <FormField
          label="Email liên hệ"
          optional
          error={errors.contactEmail?.message}
          description="Nơi nhận email mời tiếp quản + báo cáo ứng viên (doanh nghiệp tạm). Khác email chủ tài khoản."
        >
          <Input
            invalid={!!errors.contactEmail}
            placeholder="VD: quan@gmail.com"
            {...register("contactEmail")}
          />
        </FormField>
        {/* Native submit so Enter works; the footer button submits too. */}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}

function CreateInviteDialog({
  open,
  onClose,
  loading,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onSubmit: (v: { email: string; ttlHours: number }) => void;
}) {
  const t = useTranslations("admin");
  const [email, setEmail] = React.useState("");
  const [ttl, setTtl] = React.useState("168");
  React.useEffect(() => {
    if (!open) {
      setEmail("");
      setTtl("168");
    }
  }, [open]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("inviteOwner")}
      description={t("inviteOwnerDesc")}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Huỷ
          </Button>
          <Button
            disabled={!email.includes("@")}
            loading={loading}
            onClick={() =>
              onSubmit({ email: email.trim(), ttlHours: Number(ttl) || 168 })
            }
          >
            Gửi
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <FormField label={t("ownerEmail")}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="chu-quan@example.com"
          />
        </FormField>
        <FormField label={t("ttlHours")}>
          <Input
            type="number"
            value={ttl}
            onChange={(e) => setTtl(e.target.value)}
            min={1}
            max={720}
          />
        </FormField>
      </div>
    </Dialog>
  );
}
